import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RouteData } from './models/route-data';

@Injectable({
  providedIn: 'root',
})
export class RouteTitleService {
  private appTitle = new BehaviorSubject<string>('Kyudo Apps');
  private pageTitle = new BehaviorSubject<string>('');

  appTitle$ = this.appTitle.asObservable().pipe(distinctUntilChanged());

  pageTitle$ = this.pageTitle
    .asObservable()
    .pipe(debounceTime(100), distinctUntilChanged());

  /**
   * For routes determined by query parameter (ref: changeRouteOnQueryParams on the route data)
   * this map maintains the association between the id of the page and the page title.
   *
   * Example: spotlight popups -> spotlight app changes view based on the popupId
   * The title of the page is determined by the name of the popup.
   * Suppose the popups configured for the app are 3:
   * 1. Nutrition (id=nutrition)
   * 2. Specifications (id=specifications)
   * 3. Our Story (id=our-story)
   *
   * This map will have three entries
   * key="nutrition", value="Nutrition"
   * key="specifications", value="Specifications"
   * key="our-story", value="Our Story"
   *
   * Using this map, the getPageTitle function will be able to determine the title of pages
   * that changes based on query parameters.
   */
  private queryParamsRoutesPageTitles = new Map<string, string>();

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private router: Router
  ) {
    combineLatest([this.appTitle$, this.pageTitle$]).subscribe((res) => {
      this.title.setTitle(res.filter((x) => !!x).join(' | '));
    });
  }

  init() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        let pageTitle = this.getPageTitle();
        this.setPageTitle(pageTitle);
      }
    });
  }

  initQueryParamsRoutes(values: { id: string; title: string }[]) {
    this.queryParamsRoutesPageTitles.clear();
    values.forEach((v) => {
      this.queryParamsRoutesPageTitles.set(v.id, v.title);
    });
  }

  setAppTitle(appTitle: string) {
    this.appTitle.next(appTitle);
  }

  setPageTitle(pageTitle: string) {
    this.pageTitle.next(pageTitle);
  }

  /**
   * Calculate the pageTitle by reading the data of a route.
   * pageTitle needs to be explicitely assigned for each route
   * @returns
   */
  public getPageTitle() {
    let child = this.route.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.snapshot.data) {
        const data = child.snapshot.data as RouteData;
        // Standard case - pageTitle is declared in the routing file
        if (data.changeRouteOnQueryParams?.length) {
          // Extract the query paramIds from the route
          const queryParamsFromTheRoute = this.route.snapshot.queryParams;
          const queryParamIdsFromTheRoute = Object.keys(
            queryParamsFromTheRoute
          );

          // Although the route can contain multiple query params, there can only be one title hence extract the first match
          const queryParamIdAndPageData = data.changeRouteOnQueryParams.find(
            (x) => queryParamIdsFromTheRoute.includes(x.paramId)
          );

          // Extract the query param value from the query params of the route
          const queryParamVal =
            queryParamsFromTheRoute[queryParamIdAndPageData?.paramId];

          if (queryParamVal) {
            // If the page name is not based on the title return the page name from the page data eg: In the case of golden ticket prizes and loss pages
            if (!queryParamIdAndPageData.basePageTitleOnParamValue) {
              const pageNameFromPageData = queryParamIdAndPageData.pageName;
              return (
                pageNameFromPageData.charAt(0).toUpperCase() +
                pageNameFromPageData.slice(1)
              );
            }

            let pageName = this.queryParamsRoutesPageTitles.get(queryParamVal);

            // Calculate the page title if the page using the parmeter value
            if (!pageName) {
              pageName =
                queryParamVal.charAt(0).toUpperCase() + queryParamVal.slice(1);
              console.warn(
                `The page ${queryParamVal} does not have a route title setup. Calculated title: ${pageName}`
              );
            }

            return pageName;
          } else {
            return data.pageTitle || null;
          }
        } else if (data.pageTitle) {
          return data.pageTitle;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
  }
}
